/**
 * Cascade-policy wire shapes (Phase B.3 §4.6 admin policy).
 *
 * Mirrors backend internal/identity/cascadepolicy/handler.go.
 *
 * The vocabulary:
 *   - `cascade_order` — admin's preferred order (subset, ordered)
 *   - `default_order` — legacy fallback (returned for reference)
 *   - `final_order`   — what loginflow uses (composed)
 *
 * Only three methods are reorderable: passkey (always first)
 * and admin_recovery (always last) are NOT part of the
 * vocabulary — both are pinned by schema CHECK constraints.
 */

import { z } from "zod";

export const cascadeMethodSchema = z.enum([
  "totp",
  "recovery_code",
  "recovery_email",
]);
export type CascadeMethod = z.infer<typeof cascadeMethodSchema>;

export const cascadePolicySchema = z.object({
  cascade_order: z.array(cascadeMethodSchema),
  default_order: z.array(cascadeMethodSchema),
  final_order: z.array(cascadeMethodSchema),
});
export type CascadePolicy = z.infer<typeof cascadePolicySchema>;

export const setCascadePolicyRequestSchema = z.object({
  cascade_order: z.array(cascadeMethodSchema),
});
export type SetCascadePolicyRequest = z.infer<typeof setCascadePolicyRequestSchema>;

// ── Human-readable labels ─────────────────────────────────────

export const CASCADE_METHOD_LABELS: Record<CascadeMethod, string> = {
  totp: "Authenticator app (TOTP)",
  recovery_code: "Recovery codes",
  recovery_email: "Recovery email",
};

export const CASCADE_METHOD_DESCRIPTIONS: Record<CascadeMethod, string> = {
  totp: "RFC 6238 time-based codes from Google Authenticator / 1Password / Authy.",
  recovery_code: "Single-use printed codes the user generated and stored offline.",
  recovery_email: "A 6-digit code emailed to the user's recovery address.",
};
